import React, {
  createContext,
  Dispatch,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { atomWithStorage } from "jotai/utils";
import { useAtom } from "jotai";
import { SetStateAction } from "jotai/vanilla";

const defaultLimit = 10;
const minLimit = 1;

const limits = atomWithStorage<Record<string, number | undefined>>(
  "pagination.limits",
  {
    "": defaultLimit,
  },
);

const PaginationContext = createContext<{
  offset: number;
  setOffset: Dispatch<SetStateAction<number>>;
  limit: number;
  setLimit: (limit: number | undefined) => void;
  options: number[];
}>(undefined!);

interface PaginationProviderProps {
  settingsKey: string;
  options?: number[];
  defaultValue?: number;
  children: React.ReactNode;
}
export function PaginationProvider({
  settingsKey,
  options = [10, 25, 50, 100],
  defaultValue,
  children,
}: PaginationProviderProps) {
  const [offset, setOffset] = useState(0);
  const [storedLimits, setStoredLimits] = useAtom(limits);
  const sanitizedDefaultValue = useMemo(
    () =>
      defaultValue && options.includes(defaultValue)
        ? defaultValue
        : (options[0] ?? defaultLimit),
    [defaultValue, options],
  );

  const limit = useMemo(
    () =>
      storedLimits[settingsKey] ??
      sanitizedDefaultValue ??
      storedLimits[""] ??
      defaultLimit,
    [settingsKey, storedLimits, sanitizedDefaultValue],
  );

  const setLimit = useCallback(
    (newLimit: number | undefined) => {
      setStoredLimits((prev) => ({
        ...prev,
        [settingsKey]:
          newLimit === undefined ? undefined : Math.max(newLimit, minLimit),
      }));
    },
    [settingsKey, setStoredLimits],
  );

  return (
    <PaginationContext.Provider
      value={{ offset, setOffset, limit, setLimit, options }}
    >
      {children}
    </PaginationContext.Provider>
  );
}

export function withPagination<TProps>(
  providerProps: Omit<PaginationProviderProps, "children">,
  Component:
    | React.ComponentType<TProps & JSX.IntrinsicAttributes>
    | React.FC<TProps & JSX.IntrinsicAttributes>,
) {
  return function WrappedWithPagination(
    props: TProps & JSX.IntrinsicAttributes,
  ) {
    return (
      <PaginationProvider {...providerProps}>
        <Component {...props} />
      </PaginationProvider>
    );
  };
}

export const usePagination = () => {
  const context = useContext(PaginationContext);

  if (context === undefined) {
    throw new Error("usePagination must be used within a PaginationProvider");
  }

  return context;
};
